import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './CakePublicShop.css';

const DEFAULT_OCCASIONS = [
  'Birthday',
  'Anniversary',
  'Wedding',
  'Engagement',
  'Baby Shower',
  'Kids Party',
  'Corporate',
  'Other',
];

const safeText = (value) => String(value || '').trim();

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatMoney = (value) =>
  `₹${toNumber(value).toLocaleString('en-IN')}`;

const normalizeTime = (value) => {
  const text = safeText(value);
  if (!text) return '';

  const match = text.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return text;

  let hour = Number(match[1]);
  const minute = match[2];
  const suffix = hour >= 12 ? 'PM' : 'AM';
  hour %= 12;
  if (hour === 0) hour = 12;

  return `${hour}:${minute} ${suffix}`;
};


const CAKE_REFERENCE_DB = 'vakra_cake_reference_db';
const CAKE_REFERENCE_STORE = 'reference_images';

const openCakeReferenceDb = () =>
  new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('Reference image storage is not supported on this device.'));
      return;
    }

    const request = window.indexedDB.open(CAKE_REFERENCE_DB, 1);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(CAKE_REFERENCE_STORE)) {
        db.createObjectStore(CAKE_REFERENCE_STORE);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error || new Error('Unable to open reference image storage.'));
  });

const saveCakeReferenceFile = async (key, file) => {
  if (!key || !file) return;

  const db = await openCakeReferenceDb();

  await new Promise((resolve, reject) => {
    const transaction = db.transaction(CAKE_REFERENCE_STORE, 'readwrite');
    const store = transaction.objectStore(CAKE_REFERENCE_STORE);

    store.put(
      {
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        blob: file,
      },
      key
    );

    transaction.oncomplete = resolve;
    transaction.onerror = () =>
      reject(transaction.error || new Error('Reference image save failed.'));
  });

  db.close();
};

const deleteCakeReferenceFile = async (key) => {
  if (!key || !window.indexedDB) return;

  try {
    const db = await openCakeReferenceDb();

    await new Promise((resolve, reject) => {
      const transaction = db.transaction(CAKE_REFERENCE_STORE, 'readwrite');
      transaction.objectStore(CAKE_REFERENCE_STORE).delete(key);
      transaction.oncomplete = resolve;
      transaction.onerror = () =>
        reject(transaction.error || new Error('Reference image delete failed.'));
    });

    db.close();
  } catch (error) {
    console.warn('Reference image cleanup failed:', error);
  }
};

const CakePublicShop = () => {
  const { shopKey } = useParams();
  const navigate = useNavigate();

  const [shop, setShop] = useState(null);
  const [cakes, setCakes] = useState([]);
  const [otherItems, setOtherItems] = useState([]);
  const [categories, setCategories] = useState([]);

  const [activeType, setActiveType] = useState('cakes');
  const [activeCakeCategory, setActiveCakeCategory] = useState('all');
  const [activeOtherCategory, setActiveOtherCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recommended');

  const [selectedCake, setSelectedCake] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedFlavor, setSelectedFlavor] = useState('');
  const [cakeMessage, setCakeMessage] = useState('');
  const [specialRequest, setSpecialRequest] = useState('');
  const [detailQty, setDetailQty] = useState(1);

  const [occasion, setOccasion] = useState('Birthday');
  const [recipientName, setRecipientName] = useState('');
  const [ageNumber, setAgeNumber] = useState('');
  const [themeText, setThemeText] = useState('');
  const [preferredColor, setPreferredColor] = useState('');
  const [referenceImage, setReferenceImage] = useState(null);
  const [referencePreview, setReferencePreview] = useState('');

  const [fulfilmentType, setFulfilmentType] = useState('delivery');
  const [requestedDate, setRequestedDate] = useState('');
  const [requestedTime, setRequestedTime] = useState('');

  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getCakeImage = (image) => {
    if (!image) return '';

    if (
      image.startsWith('http://') ||
      image.startsWith('https://') ||
      image.startsWith('data:') ||
      image.startsWith('blob:')
    ) {
      return image;
    }

    if (image.includes('/uploads/cakes/')) {
      const filename = image.split('/').pop();
      return `/api/cake/products/image/${encodeURIComponent(filename)}`;
    }

    return image.startsWith('/') ? image : `/${image}`;
  };

  const getOtherImage = (image) => {
    if (!image) return '';

    if (
      image.startsWith('http://') ||
      image.startsWith('https://') ||
      image.startsWith('data:') ||
      image.startsWith('blob:')
    ) {
      return image;
    }

    if (image.includes('/uploads/cake-shop-items/')) {
      const filename = image.split('/').pop();
      return `/api/cake/products/shop-item-image/${encodeURIComponent(filename)}`;
    }

    return image.startsWith('/') ? image : `/${image}`;
  };

  useEffect(() => {
    const loadShop = async () => {
      try {
        setLoading(true);
        setError('');

        const [response, settingsResponse] = await Promise.all([
          fetch(
            `/api/cake/products/public-shop/${encodeURIComponent(shopKey || '')}`
          ),
          fetch(
            `/api/cake/products/public-shop/${encodeURIComponent(
              shopKey || ''
            )}/settings`
          ),
        ]);

        const data = await response.json();
        const settingsData = await settingsResponse
          .json()
          .catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.message || 'Cake shop not found');
        }

        const publicSettings =
          settingsResponse.ok && settingsData?.settings
            ? settingsData.settings
            : {};

        setShop({
          ...(data.shop || {}),
          delivery_available:
            publicSettings.delivery_enabled === undefined
              ? undefined
              : Boolean(Number(publicSettings.delivery_enabled)),
          pickup_available:
            publicSettings.pickup_enabled === undefined
              ? undefined
              : Boolean(Number(publicSettings.pickup_enabled)),
          delivery_start_time:
            publicSettings.delivery_start_time || '',
          delivery_end_time:
            publicSettings.delivery_end_time || '',
          custom_order_lead_hours:
            Number(publicSettings.custom_advance_hours || 0),
          standard_order_lead_minutes:
            Number(publicSettings.regular_prep_minutes || 0),
          custom_orders_enabled:
            publicSettings.custom_orders_enabled,
          custom_delivery_start_time:
            publicSettings.custom_delivery_start_time || '',
          custom_delivery_end_time:
            publicSettings.custom_delivery_end_time || '',
          shop_open_time:
            publicSettings.shop_open_time || '',
          shop_close_time:
            publicSettings.shop_close_time || '',
        });

        setCakes(Array.isArray(data.cakes) ? data.cakes : []);
        setOtherItems(Array.isArray(data.other_items) ? data.other_items : []);
        setCategories(Array.isArray(data.categories) ? data.categories : []);
      } catch (err) {
        setError(err.message || 'Unable to load cake shop');
      } finally {
        setLoading(false);
      }
    };

    loadShop();
  }, [shopKey]);

  useEffect(() => {
    const cartKey = `vakra_cake_cart_${safeText(shopKey).toLowerCase()}`;

    try {
      const saved = localStorage.getItem(cartKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setCart(parsed);
      }
    } catch (storageError) {
      console.warn('Cake cart restore failed:', storageError);
    }
  }, [shopKey]);

  useEffect(() => {
    const cartKey = `vakra_cake_cart_${safeText(shopKey).toLowerCase()}`;

    try {
      localStorage.setItem(cartKey, JSON.stringify(cart));
    } catch (storageError) {
      console.warn('Cake cart save failed:', storageError);
    }
  }, [cart, shopKey]);

  useEffect(() => {
    return () => {
      if (referencePreview && referencePreview.startsWith('blob:')) {
        URL.revokeObjectURL(referencePreview);
      }
    };
  }, [referencePreview]);

  const cakeCategories = useMemo(() => {
    const map = new Map();

    cakes.forEach((cake) => {
      const category = safeText(cake.category);
      if (!category) return;

      const key = category.toLowerCase();
      if (!map.has(key)) map.set(key, category);
    });

    return Array.from(map.entries()).map(([key, label]) => ({
      key,
      label,
    }));
  }, [cakes]);

  const searchableCakeText = (cake) => {
    const flavors = Array.isArray(cake.flavors)
      ? cake.flavors
          .map((flavor) =>
            typeof flavor === 'string' ? flavor : flavor?.flavor_name
          )
          .filter(Boolean)
          .join(' ')
      : '';

    const variants = Array.isArray(cake.variants)
      ? cake.variants
          .map((variant) => `${variant?.weight || ''} ${variant?.price || ''}`)
          .join(' ')
      : '';

    return [
      cake.name,
      cake.category,
      cake.description,
      cake.egg_type,
      cake.shape,
      flavors,
      variants,
      cake.customization_enabled ? 'custom customized customisable designer' : '',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  };

  const filteredCakes = useMemo(() => {
    const text = search.trim().toLowerCase();

    let result = cakes.filter((cake) => {
      const categoryMatch =
        activeCakeCategory === 'all' ||
        safeText(cake.category).toLowerCase() === activeCakeCategory;

      const searchMatch = !text || searchableCakeText(cake).includes(text);

      const eggType = safeText(cake.egg_type).toLowerCase();
      const filterMatch =
        quickFilter === 'all' ||
        (quickFilter === 'eggless' &&
          (eggType === 'eggless' || eggType === 'both')) ||
        (quickFilter === 'custom' && Boolean(cake.customization_enabled));

      return categoryMatch && searchMatch && filterMatch;
    });

    if (sortBy === 'price-low') {
      result = [...result].sort(
        (a, b) => toNumber(a.starting_price) - toNumber(b.starting_price)
      );
    }

    if (sortBy === 'price-high') {
      result = [...result].sort(
        (a, b) => toNumber(b.starting_price) - toNumber(a.starting_price)
      );
    }

    return result;
  }, [cakes, search, activeCakeCategory, quickFilter, sortBy]);

  const filteredOtherItems = useMemo(() => {
    const text = search.trim().toLowerCase();

    return otherItems.filter((item) => {
      const categoryMatch =
        activeOtherCategory === 'all' ||
        String(item.category_id) === String(activeOtherCategory);

      const searchMatch =
        !text ||
        item.name?.toLowerCase().includes(text) ||
        item.category_name?.toLowerCase().includes(text) ||
        item.description?.toLowerCase().includes(text) ||
        item.unit?.toLowerCase().includes(text);

      return categoryMatch && searchMatch;
    });
  }, [otherItems, activeOtherCategory, search]);

  const customizableCakes = useMemo(
    () => cakes.filter((cake) => Boolean(cake.customization_enabled)),
    [cakes]
  );

  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + Number(item.qty || 0), 0),
    [cart]
  );

  const cartTotal = useMemo(
    () =>
      cart.reduce(
        (sum, item) =>
          sum + Number(item.price || 0) * Number(item.qty || 0),
        0
      ),
    [cart]
  );

  const vendorSettings = useMemo(() => {
    const source = shop || {};

    return {
      deliveryAvailable:
        source.delivery_available === undefined
          ? true
          : Boolean(source.delivery_available),
      pickupAvailable:
        source.pickup_available === undefined
          ? true
          : Boolean(source.pickup_available),
      deliveryStart:
        source.delivery_start_time || source.order_start_time || '',
      deliveryEnd:
        source.delivery_end_time || source.order_end_time || '',
      customLeadHours: toNumber(
        source.custom_order_lead_hours ?? source.customization_lead_hours,
        0
      ),
      standardLeadMinutes: toNumber(
        source.standard_order_lead_minutes ?? source.preparation_time_minutes,
        0
      ),
    };
  }, [shop]);

  const deliveryTimingText = useMemo(() => {
    if (vendorSettings.deliveryStart && vendorSettings.deliveryEnd) {
      return `${normalizeTime(vendorSettings.deliveryStart)} - ${normalizeTime(
        vendorSettings.deliveryEnd
      )}`;
    }

    return '';
  }, [vendorSettings]);

  const customLeadText = useMemo(() => {
    if (!vendorSettings.customLeadHours) return '';

    if (vendorSettings.customLeadHours < 24) {
      return `Custom cake ke liye kam se kam ${vendorSettings.customLeadHours} hours pehle order karein.`;
    }

    const days = vendorSettings.customLeadHours / 24;
    const rounded = Number.isInteger(days) ? days : days.toFixed(1);
    return `Custom cake ke liye kam se kam ${rounded} day pehle order karein.`;
  }, [vendorSettings]);

  const addSimpleItem = (item) => {
    const key = `other-${item.id}`;

    setCart((prev) => {
      const existing = prev.find((cartItem) => cartItem.key === key);

      if (existing) {
        return prev.map((cartItem) =>
          cartItem.key === key
            ? { ...cartItem, qty: cartItem.qty + 1 }
            : cartItem
        );
      }

      return [
        ...prev,
        {
          key,
          type: 'other',
          id: item.id,
          name: item.name,
          image: getOtherImage(item.image),
          price: Number(item.price || 0),
          qty: 1,
          unit: item.unit || '',
          category_name: item.category_name || '',
        },
      ];
    });
  };

  const resetCustomization = () => {
    setOccasion('Birthday');
    setRecipientName('');
    setAgeNumber('');
    setThemeText('');
    setPreferredColor('');
    setReferenceImage(null);
    setReferencePreview('');
    setRequestedDate('');
    setRequestedTime('');
    setFulfilmentType(
      vendorSettings.deliveryAvailable ? 'delivery' : 'pickup'
    );
  };

  const openCake = (cake) => {
    const firstVariant =
      Array.isArray(cake.variants) && cake.variants.length
        ? cake.variants[0]
        : null;

    const firstFlavor =
      Array.isArray(cake.flavors) && cake.flavors.length
        ? cake.flavors[0]?.flavor_name || cake.flavors[0] || ''
        : '';

    setSelectedCake(cake);
    setSelectedVariant(firstVariant);
    setSelectedFlavor(firstFlavor);
    setCakeMessage('');
    setSpecialRequest('');
    setDetailQty(1);
    resetCustomization();
  };

  const closeCake = () => {
    setSelectedCake(null);
    setReferenceImage(null);
    setReferencePreview('');
  };

  const handleReferenceImage = (event) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      setReferenceImage(null);
      setReferencePreview('');
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Please image file select karein.');
      event.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Reference image 5 MB se chhoti honi chahiye.');
      event.target.value = '';
      return;
    }

    if (referencePreview && referencePreview.startsWith('blob:')) {
      URL.revokeObjectURL(referencePreview);
    }

    setReferenceImage(file);
    setReferencePreview(URL.createObjectURL(file));
  };

  const addCakeToCart = async () => {
    if (!selectedCake) return;

    const price = Number(
      selectedVariant?.price || selectedCake.starting_price || 0
    );

    const variantText = selectedVariant?.weight || '';
    const flavorText = selectedFlavor || '';
    const isCustom = Boolean(selectedCake.customization_enabled);

    let referenceImageKey = '';

    if (isCustom && referenceImage) {
      referenceImageKey = [
        'cake-ref',
        safeText(shopKey).toLowerCase() || 'shop',
        selectedCake.id,
        Date.now(),
        Math.random().toString(36).slice(2, 8),
      ].join('-');

      try {
        await saveCakeReferenceFile(referenceImageKey, referenceImage);
      } catch (imageSaveError) {
        console.error('Reference image save failed:', imageSaveError);
        alert(
          'Reference image save nahi ho paayi. Please image dobara select karke try karein.'
        );
        return;
      }
    }

    const key = [
      'cake',
      selectedCake.id,
      selectedVariant?.id || variantText || 'base',
      flavorText,
      cakeMessage.trim(),
      specialRequest.trim(),
      isCustom ? occasion : '',
      isCustom ? recipientName.trim() : '',
      isCustom ? ageNumber.trim() : '',
      isCustom ? themeText.trim() : '',
      isCustom ? preferredColor.trim() : '',
      referenceImageKey,
      requestedDate,
      requestedTime,
      fulfilmentType,
    ].join('|');

    const customPayload = isCustom
      ? {
          occasion,
          recipient_name: recipientName.trim(),
          age: ageNumber.trim(),
          theme: themeText.trim(),
          preferred_color: preferredColor.trim(),
          cake_message: cakeMessage.trim(),
          special_request: specialRequest.trim(),
          reference_image_name: referenceImage?.name || '',
          reference_image_key: referenceImageKey,
        }
      : null;

    setCart((prev) => [
      ...prev,
      {
        key,
        type: 'cake',
        id: selectedCake.id,
        name: selectedCake.name,
        image: getCakeImage(selectedCake.image),
        price,
        qty: detailQty,
        variant: variantText,
        flavor: flavorText,
        cake_message: cakeMessage.trim(),
        special_request: specialRequest.trim(),
        customization: customPayload,
        requested_date: requestedDate,
        requested_time: requestedTime,
        fulfilment_type: fulfilmentType,
      },
    ]);

    closeCake();
  };

  const changeCartQty = (key, delta) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.key === key
            ? { ...item, qty: Math.max(0, item.qty + delta) }
            : item
        )
        .filter((item) => item.qty > 0)
    );
  };

  const removeCartItem = (key) => {
    setCart((prev) => {
      const itemToRemove = prev.find((item) => item.key === key);
      const referenceKey = itemToRemove?.customization?.reference_image_key;

      if (referenceKey) {
        deleteCakeReferenceFile(referenceKey);
      }

      return prev.filter((item) => item.key !== key);
    });
  };

  const clearSearch = () => {
    setSearch('');
    setActiveCakeCategory('all');
    setActiveOtherCategory('all');
    setQuickFilter('all');
  };

  if (loading) {
    return (
      <div className="cake-public-state-page">
        <div className="cake-public-loader">🎂</div>
        <h2>Loading Cake Shop...</h2>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="cake-public-state-page">
        <div className="cake-public-loader">!</div>
        <h2>Shop Not Found</h2>
        <p>{error || 'This cake shop link is not available.'}</p>
      </div>
    );
  }

  return (
    <div className="cake-public-page">
      <div className="cake-public-shell">
        <header className="cake-public-topbar">
          <div className="cake-public-brand-mark">V</div>
          <strong>VAKRA CAKE</strong>
          <button
            type="button"
            className="cake-public-cart-icon"
            onClick={() => setShowCart(true)}
            aria-label="Open cart"
          >
            🛒
            {cartCount > 0 && <span>{cartCount}</span>}
          </button>
        </header>

        <section className="cake-public-shop-head">
          <div className="cake-public-shop-main">
            <div className="cake-public-logo">
              {shop.shop_logo ? (
                <img src={shop.shop_logo} alt={shop.shop_name} />
              ) : (
                <span>🎂</span>
              )}
            </div>

            <div className="cake-public-shop-copy">
              <p className="cake-public-eyebrow">Cake Shop</p>
              <h1>{shop.shop_name}</h1>
              {shop.location && <p className="cake-public-location">📍 {shop.location}</p>}
            </div>
          </div>

          <div className="cake-public-shop-meta">
            {shop.rating && <span>⭐ {shop.rating}</span>}
            {deliveryTimingText && <span>🕒 {deliveryTimingText}</span>}
            {vendorSettings.deliveryAvailable && <span>🛵 Delivery</span>}
            {vendorSettings.pickupAvailable && <span>🏪 Pickup</span>}
          </div>

          {(customLeadText || vendorSettings.standardLeadMinutes > 0) && (
            <div className="cake-public-service-note">
              {vendorSettings.standardLeadMinutes > 0 && (
                <span>
                  Regular order prep: ~{vendorSettings.standardLeadMinutes} min
                </span>
              )}
              {customLeadText && <span>{customLeadText}</span>}
            </div>
          )}
        </section>

        <section className="cake-public-discovery">
          <div className="cake-public-search">
            <span>⌕</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search cake, flavor, category, weight..."
            />
            {search && (
              <button type="button" onClick={clearSearch} aria-label="Clear search">
                ×
              </button>
            )}
          </div>

          <div className="cake-public-type-tabs">
            <button
              type="button"
              className={activeType === 'cakes' ? 'active' : ''}
              onClick={() => setActiveType('cakes')}
            >
              🎂 Cakes
            </button>
            <button
              type="button"
              className={activeType === 'other' ? 'active' : ''}
              onClick={() => setActiveType('other')}
            >
              🛍️ Other Items
            </button>
          </div>

          {activeType === 'cakes' && (
            <>
              <div className="cake-public-category-scroll cake-public-cake-categories">
                <button
                  type="button"
                  className={activeCakeCategory === 'all' ? 'active' : ''}
                  onClick={() => setActiveCakeCategory('all')}
                >
                  All Cakes
                </button>

                {cakeCategories.map((category) => (
                  <button
                    type="button"
                    key={category.key}
                    className={
                      activeCakeCategory === category.key ? 'active' : ''
                    }
                    onClick={() => setActiveCakeCategory(category.key)}
                  >
                    {category.label}
                  </button>
                ))}
              </div>

              <div className="cake-public-filter-row">
                <div className="cake-public-quick-filters">
                  <button
                    type="button"
                    className={quickFilter === 'all' ? 'active' : ''}
                    onClick={() => setQuickFilter('all')}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={quickFilter === 'eggless' ? 'active' : ''}
                    onClick={() => setQuickFilter('eggless')}
                  >
                    Eggless
                  </button>
                  {customizableCakes.length > 0 && (
                    <button
                      type="button"
                      className={quickFilter === 'custom' ? 'active' : ''}
                      onClick={() => setQuickFilter('custom')}
                    >
                      ✨ Custom Cakes
                    </button>
                  )}
                </div>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort cakes"
                >
                  <option value="recommended">Recommended</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
            </>
          )}
        </section>

        {activeType === 'cakes' ? (
          <main className="cake-public-content">
            {customizableCakes.length > 0 && quickFilter !== 'custom' && !search && (
              <section className="cake-public-custom-banner">
                <div>
                  <small>MAKE IT YOUR WAY</small>
                  <h2>Custom Cake Order</h2>
                  <p>
                    Occasion, name, age, theme, color aur reference design ke saath
                    cake customize karein.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setQuickFilter('custom')}
                >
                  Explore Custom Cakes
                </button>
              </section>
            )}

            <div className="cake-public-section-title">
              <div>
                <h2>
                  {quickFilter === 'custom'
                    ? 'Customizable Cakes'
                    : activeCakeCategory !== 'all'
                    ? cakeCategories.find(
                        (category) => category.key === activeCakeCategory
                      )?.label || 'Cakes'
                    : 'Cakes'}
                </h2>
                <small>
                  {filteredCakes.length} option{filteredCakes.length === 1 ? '' : 's'}
                </small>
              </div>
            </div>

            {filteredCakes.length === 0 ? (
              <div className="cake-public-empty">
                <span>🎂</span>
                <h3>No cakes found</h3>
                <p>Search ya filters change karke dekhein.</p>
                <button type="button" onClick={clearSearch}>
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="cake-public-cake-grid">
                {filteredCakes.map((cake) => {
                  const flavorCount = Array.isArray(cake.flavors)
                    ? cake.flavors.length
                    : 0;

                  const variantCount = Array.isArray(cake.variants)
                    ? cake.variants.length
                    : 0;

                  return (
                    <article className="cake-public-cake-card" key={cake.id}>
                      <button
                        type="button"
                        className="cake-public-cake-photo"
                        onClick={() => openCake(cake)}
                      >
                        {cake.image ? (
                          <img src={getCakeImage(cake.image)} alt={cake.name} />
                        ) : (
                          <span>🎂</span>
                        )}

                        {Boolean(cake.customization_enabled) && (
                          <b className="cake-public-card-badge">Customizable</b>
                        )}
                      </button>

                      <div className="cake-public-cake-info">
                        <div className="cake-public-cake-title-row">
                          <h3>{cake.name}</h3>
                        </div>

                        <div className="cake-public-card-tags">
                          <small>
                            {cake.egg_type === 'egg'
                              ? 'With Egg'
                              : cake.egg_type === 'both'
                              ? 'Egg / Eggless'
                              : 'Eggless'}
                          </small>
                          {safeText(cake.category) && (
                            <small>{safeText(cake.category)}</small>
                          )}
                        </div>

                        <div className="cake-public-card-options">
                          {variantCount > 0 && <span>{variantCount} sizes</span>}
                          {flavorCount > 0 && <span>{flavorCount} flavors</span>}
                        </div>

                        <div className="cake-public-card-footer">
                          <div>
                            <p>Starts from</p>
                            <strong>{formatMoney(cake.starting_price)}</strong>
                          </div>

                          <button type="button" onClick={() => openCake(cake)}>
                            Select
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </main>
        ) : (
          <main className="cake-public-content">
            <div className="cake-public-category-scroll">
              <button
                type="button"
                className={activeOtherCategory === 'all' ? 'active' : ''}
                onClick={() => setActiveOtherCategory('all')}
              >
                All
              </button>

              {categories
                .filter((category) => category.status === 'active')
                .map((category) => (
                  <button
                    type="button"
                    key={category.id}
                    className={
                      String(activeOtherCategory) === String(category.id)
                        ? 'active'
                        : ''
                    }
                    onClick={() => setActiveOtherCategory(String(category.id))}
                  >
                    {category.category_name}
                  </button>
                ))}
            </div>

            <div className="cake-public-section-title">
              <div>
                <h2>Other Shop Items</h2>
                <small>{filteredOtherItems.length} options</small>
              </div>
            </div>

            <div className="cake-public-other-list">
              {filteredOtherItems.length === 0 ? (
                <div className="cake-public-empty">
                  <span>🛍️</span>
                  <h3>No items found</h3>
                </div>
              ) : (
                filteredOtherItems.map((item) => (
                  <article className="cake-public-other-row" key={item.id}>
                    <div className="cake-public-other-image">
                      {item.image ? (
                        <img src={getOtherImage(item.image)} alt={item.name} />
                      ) : (
                        <span>🛍️</span>
                      )}
                    </div>

                    <div className="cake-public-other-info">
                      <h3>{item.name}</h3>
                      <p>{item.category_name || 'Other Item'}</p>
                      {item.unit && <small>{item.unit}</small>}
                      <strong>{formatMoney(item.price)}</strong>
                    </div>

                    <button
                      type="button"
                      className="cake-public-add-button"
                      disabled={item.availability !== 'available'}
                      onClick={() => addSimpleItem(item)}
                    >
                      {item.availability === 'available' ? '+ Add' : 'Out'}
                    </button>
                  </article>
                ))
              )}
            </div>
          </main>
        )}

        {cartCount > 0 && (
          <button
            type="button"
            className="cake-public-cart-bar"
            onClick={() => setShowCart(true)}
          >
            <span>
              🛒
              <small>{cartCount} item{cartCount === 1 ? '' : 's'} in Cart</small>
              <strong>{formatMoney(cartTotal)}</strong>
            </span>

            <b>View Cart</b>
          </button>
        )}
      </div>

      {selectedCake && (
        <div
          className="cake-public-modal-overlay"
          onClick={closeCake}
          role="presentation"
        >
          <section
            className="cake-public-detail"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cake-public-detail-head">
              <button
                type="button"
                className="cake-public-detail-back"
                onClick={closeCake}
              >
                ← Back
              </button>
              <strong>{selectedCake.name}</strong>
              <span className="cake-public-detail-head-spacer" />
            </div>

            <div className="cake-public-detail-image">
              {selectedCake.image || selectedCake.image_url ? (
                <img
                  src={
                    selectedCake.image_url || getCakeImage(selectedCake.image)
                  }
                  alt={selectedCake.name}
                />
              ) : (
                <span>🎂</span>
              )}
            </div>

            <div className="cake-public-detail-body">
              <div className="cake-public-detail-title-row">
                <div>
                  <p>{safeText(selectedCake.category) || 'Cake'}</p>
                  <h2>{selectedCake.name}</h2>
                </div>
                <strong>{formatMoney(selectedVariant?.price || selectedCake.starting_price)}</strong>
              </div>

              <div className="cake-public-detail-tags">
                <span>
                  {selectedCake.egg_type === 'egg'
                    ? 'With Egg'
                    : selectedCake.egg_type === 'both'
                    ? 'Egg / Eggless'
                    : 'Eggless'}
                </span>
                {safeText(selectedCake.shape) && (
                  <span>{safeText(selectedCake.shape)}</span>
                )}
                {Boolean(selectedCake.customization_enabled) && (
                  <span className="pink">Customizable</span>
                )}
              </div>

              {selectedCake.description && (
                <p className="cake-public-detail-description">
                  {selectedCake.description}
                </p>
              )}

              <div className="cake-public-detail-section">
                <div className="cake-public-section-heading-row">
                  <h3>1. Choose Size / Weight</h3>
                  <small>
                    {Array.isArray(selectedCake.variants)
                      ? `${selectedCake.variants.length} options`
                      : ''}
                  </small>
                </div>

                {Array.isArray(selectedCake.variants) &&
                selectedCake.variants.length > 0 ? (
                  <div className="cake-public-variant-grid">
                    {selectedCake.variants.map((variant) => (
                      <button
                        type="button"
                        key={variant.id || variant.weight}
                        className={
                          selectedVariant?.id === variant.id ||
                          (!selectedVariant?.id &&
                            selectedVariant?.weight === variant.weight)
                            ? 'active'
                            : ''
                        }
                        onClick={() => setSelectedVariant(variant)}
                      >
                        <span>{variant.weight || 'Standard'}</span>
                        <strong>{formatMoney(variant.price)}</strong>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="cake-public-base-price">
                    <span>Starting Price</span>
                    <strong>{formatMoney(selectedCake.starting_price)}</strong>
                  </div>
                )}
              </div>

              {Array.isArray(selectedCake.flavors) &&
                selectedCake.flavors.length > 0 && (
                  <div className="cake-public-detail-section">
                    <div className="cake-public-section-heading-row">
                      <h3>2. Choose Flavor</h3>
                      <small>{selectedCake.flavors.length} flavors</small>
                    </div>

                    <div className="cake-public-flavor-grid">
                      {selectedCake.flavors.map((flavor) => {
                        const name =
                          typeof flavor === 'string'
                            ? flavor
                            : flavor.flavor_name;

                        return (
                          <button
                            type="button"
                            key={flavor.id || name}
                            className={selectedFlavor === name ? 'active' : ''}
                            onClick={() => setSelectedFlavor(name)}
                          >
                            {name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

              {Boolean(selectedCake.customization_enabled) && (
                <div className="cake-public-detail-section cake-public-custom-section">
                  <div className="cake-public-section-heading-row">
                    <div>
                      <h3>3. Customize Your Cake</h3>
                      <small>Optional details - vendor final confirmation karega</small>
                    </div>
                  </div>

                  {customLeadText && (
                    <div className="cake-public-custom-lead-note">
                      ⏳ {customLeadText}
                    </div>
                  )}

                  <div className="cake-public-form-grid">
                    <label>
                      <span>Occasion</span>
                      <select value={occasion} onChange={(e) => setOccasion(e.target.value)}>
                        {DEFAULT_OCCASIONS.map((item) => (
                          <option value={item} key={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label>
                      <span>Name on Cake</span>
                      <input
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                        placeholder="e.g. Aarav"
                        maxLength={50}
                      />
                    </label>

                    <label>
                      <span>Age / Number</span>
                      <input
                        value={ageNumber}
                        onChange={(e) => setAgeNumber(e.target.value)}
                        placeholder="e.g. 5, 18, 25"
                        maxLength={10}
                      />
                    </label>

                    <label>
                      <span>Preferred Color</span>
                      <input
                        value={preferredColor}
                        onChange={(e) => setPreferredColor(e.target.value)}
                        placeholder="e.g. Pink & White"
                        maxLength={50}
                      />
                    </label>

                    <label className="full">
                      <span>Theme / Design Idea</span>
                      <textarea
                        rows="3"
                        value={themeText}
                        onChange={(e) => setThemeText(e.target.value)}
                        placeholder="Princess theme, football theme, floral design..."
                        maxLength={250}
                      />
                    </label>

                    <label className="full cake-public-reference-upload">
                      <span>Reference Design Image</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleReferenceImage}
                      />
                      <small>JPG/PNG/WEBP up to 5 MB</small>

                      {referencePreview && (
                        <div className="cake-public-reference-preview">
                          <img src={referencePreview} alt="Reference design preview" />
                          <button
                            type="button"
                            onClick={() => {
                              setReferenceImage(null);
                              setReferencePreview('');
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              )}

              {Boolean(selectedCake.cake_message_enabled) && (
                <div className="cake-public-detail-section">
                  <label className="cake-public-detail-label">
                    Cake Message <small>(Optional)</small>
                  </label>
                  <input
                    className="cake-public-detail-input"
                    value={cakeMessage}
                    maxLength={80}
                    onChange={(e) => setCakeMessage(e.target.value)}
                    placeholder="Happy Birthday..."
                  />
                </div>
              )}

              {Boolean(selectedCake.customization_enabled) && (
                <div className="cake-public-detail-section">
                  <label className="cake-public-detail-label">
                    Special Request <small>(Optional)</small>
                  </label>
                  <textarea
                    className="cake-public-detail-input"
                    rows="3"
                    value={specialRequest}
                    onChange={(e) => setSpecialRequest(e.target.value)}
                    placeholder="Any allergy note, decoration request, less sweet, etc."
                    maxLength={300}
                  />
                </div>
              )}

              <div className="cake-public-detail-section cake-public-delivery-section">
                <div className="cake-public-section-heading-row">
                  <div>
                    <h3>
                      {Boolean(selectedCake.customization_enabled) ? '4.' : '3.'}{' '}
                      Preferred Delivery / Pickup
                    </h3>
                    <small>Final slot availability vendor confirm karega</small>
                  </div>
                </div>

                <div className="cake-public-fulfilment-toggle">
                  {vendorSettings.deliveryAvailable && (
                    <button
                      type="button"
                      className={fulfilmentType === 'delivery' ? 'active' : ''}
                      onClick={() => setFulfilmentType('delivery')}
                    >
                      🛵 Delivery
                    </button>
                  )}

                  {vendorSettings.pickupAvailable && (
                    <button
                      type="button"
                      className={fulfilmentType === 'pickup' ? 'active' : ''}
                      onClick={() => setFulfilmentType('pickup')}
                    >
                      🏪 Pickup
                    </button>
                  )}
                </div>

                <div className="cake-public-delivery-grid">
                  <label>
                    <span>Preferred Date</span>
                    <input
                      type="date"
                      value={requestedDate}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setRequestedDate(e.target.value)}
                    />
                  </label>

                  <label>
                    <span>Preferred Time</span>
                    <input
                      type="time"
                      value={requestedTime}
                      onChange={(e) => setRequestedTime(e.target.value)}
                    />
                  </label>
                </div>

                {deliveryTimingText && (
                  <p className="cake-public-delivery-window">
                    Vendor delivery window: <strong>{deliveryTimingText}</strong>
                  </p>
                )}
              </div>

              <div className="cake-public-detail-qty">
                <div>
                  <strong>Quantity</strong>
                  <small>Kitne cake chahiye?</small>
                </div>

                <div className="cake-public-qty-control">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    onClick={() => setDetailQty((qty) => Math.max(1, qty - 1))}
                  >
                    −
                  </button>
                  <span>{detailQty}</span>
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    onClick={() => setDetailQty((qty) => qty + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="cake-public-detail-add"
              onClick={addCakeToCart}
            >
              Add to Cart&nbsp;&nbsp;
              {formatMoney(
                Number(
                  selectedVariant?.price || selectedCake.starting_price || 0
                ) * detailQty
              )}
            </button>
          </section>
        </div>
      )}

      {showCart && (
        <div
          className="cake-public-modal-overlay cart-overlay"
          onClick={() => setShowCart(false)}
          role="presentation"
        >
          <section
            className="cake-public-cart-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cake-public-cart-head">
              <button type="button" onClick={() => setShowCart(false)}>
                ←
              </button>
              <strong>Your Cart</strong>
              <span>{cartCount}</span>
            </div>

            <div className="cake-public-cart-items">
              {cart.length === 0 ? (
                <div className="cake-public-empty">
                  <span>🛒</span>
                  <h3>Your cart is empty</h3>
                </div>
              ) : (
                cart.map((item) => (
                  <article className="cake-public-cart-row" key={item.key}>
                    <div className="cake-public-cart-image">
                      {item.image ? (
                        <img src={item.image} alt={item.name} />
                      ) : (
                        <span>🎂</span>
                      )}
                    </div>

                    <div className="cake-public-cart-info">
                      <h3>{item.name}</h3>
                      <p>
                        {[item.variant, item.flavor, item.unit]
                          .filter(Boolean)
                          .join(' • ')}
                      </p>

                      {item.customization && (
                        <div className="cake-public-cart-custom-summary">
                          <small className="cake-public-cart-custom-text">
                            ✨ {[
                              item.customization.occasion,
                              item.customization.recipient_name,
                              item.customization.theme,
                            ]
                              .filter(Boolean)
                              .join(' • ')}
                          </small>

                          {item.cake_message && (
                            <small className="cake-public-cart-custom-text">
                              📝 Message: {item.cake_message}
                            </small>
                          )}

                          {item.special_request && (
                            <small className="cake-public-cart-custom-text">
                              💬 Request: {item.special_request}
                            </small>
                          )}

                          {item.customization.reference_image_name && (
                            <small className="cake-public-cart-custom-text">
                              🖼️ Reference image attached
                            </small>
                          )}
                        </div>
                      )}

                      {(item.requested_date || item.requested_time) && (
                        <small className="cake-public-cart-custom-text">
                          🕒 {item.fulfilment_type === 'pickup' ? 'Pickup' : 'Delivery'}:{' '}
                          {[item.requested_date, item.requested_time]
                            .filter(Boolean)
                            .join(' ')}
                        </small>
                      )}

                      <strong>
                        {formatMoney(
                          Number(item.price || 0) * Number(item.qty || 0)
                        )}
                      </strong>
                    </div>

                    <div className="cake-public-cart-actions">
                      <div className="cake-public-cart-qty">
                        <button
                          type="button"
                          aria-label="Decrease quantity"
                          onClick={() => changeCartQty(item.key, -1)}
                        >
                          −
                        </button>
                        <span>{item.qty}</span>
                        <button
                          type="button"
                          aria-label="Increase quantity"
                          onClick={() => changeCartQty(item.key, 1)}
                        >
                          +
                        </button>
                      </div>

                      <button
                        type="button"
                        className="cake-public-remove-item"
                        onClick={() => removeCartItem(item.key)}
                      >
                        Remove
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="cake-public-cart-summary">
                <div>
                  <span>Subtotal</span>
                  <strong>{formatMoney(cartTotal)}</strong>
                </div>

                <div className="total">
                  <span>Total</span>
                  <strong>{formatMoney(cartTotal)}</strong>
                </div>

                <p className="cake-public-checkout-note">
                  Delivery charge, final custom-design price aur exact slot checkout/vendor confirmation par update ho sakta hai.
                </p>

                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/cake/${encodeURIComponent(shopKey || '')}/checkout`,
                      { state: { fromCart: true } }
                    )
                  }
                >
                  Proceed to Checkout
                </button>
              </div>
            )}
          </section>
        </div>
      )}
      <nav className="cake-public-mobile-nav">
        <button
          type="button"
          className={activeType === 'cakes' && quickFilter === 'all' ? 'active' : ''}
          onClick={() => {
            setActiveType('cakes');
            setQuickFilter('all');
            setActiveCakeCategory('all');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <span>⌂</span>
          <small>Home</small>
        </button>

        <button
          type="button"
          className={activeType === 'cakes' ? 'active' : ''}
          onClick={() => {
            setActiveType('cakes');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <span>▦</span>
          <small>Categories</small>
        </button>

        <button
          type="button"
          className="custom-center"
          onClick={() => {
            setActiveType('cakes');
            setQuickFilter('custom');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <span>＋</span>
          <small>Custom</small>
        </button>

        <button type="button" onClick={() => setShowCart(true)}>
          <span>🛒</span>
          <small>Cart</small>
        </button>

        <button
          type="button"
          className={activeType === 'other' ? 'active' : ''}
          onClick={() => {
            setActiveType('other');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <span>🎁</span>
          <small>More</small>
        </button>
      </nav>

    </div>
  );
};

export default CakePublicShop;
